import { WidgetTypeMeta } from './widgetTypes.js';

/**
 * Basisklasse für Dashboard Widgets.
 * Subklassen müssen mindestens onRender(bodyEl) implementieren.
 * Optional können getTitle/getIcon überschrieben werden, fallen sonst auf
 * die Werte aus WidgetTypeMeta für den Widget-Typ zurück.
 */
export default class BaseWidget {
    /**
     * @param {string} type - Wert aus WidgetType
     * @param {string} instanceId - eindeutige Widget-Instanz-ID
     * @param {object} [state] - persistierter Zustand der Instanz
     */
    constructor(type, instanceId, state = {}) {
        this.type = type;
        this.instanceId = instanceId;
        this.state = state;
        this.bodyEl = null;
    }

    getTitle() {
        return WidgetTypeMeta[this.type]?.name || 'Widget';
    }

    getIcon() {
        return WidgetTypeMeta[this.type]?.icon || 'bi-grid';
    }

    /**
     * Wird vom Dashboard nach dem Erzeugen des Tile aufgerufen.
     */
    async render(bodyEl) {
        this.bodyEl = bodyEl;
        await this.onRender();
    }

    /**
     * Muss von Subklassen implementiert werden.
     */
    async onRender() {
        throw new Error('BaseWidget.onRender must be implemented by subclass');
    }

    async refresh() {
        if (this.bodyEl) {
            await this.onRender();
        }
    }

    destroy() {}

    /**
     * Aktuelle Grid-Zeilen-Belegung: zuerst Nutzer-Override aus dem persistierten State,
     * sonst widget-spezifischer Default aus getDefaultRowSpan().
     * @returns {1 | 2}
     */
    getRowSpan() {
        const override = this.state?.rowSpan;
        if (override === 1 || override === 2) return override;
        return this.getDefaultRowSpan();
    }

    /**
     * Standardgröße eines Widgets ohne Nutzeranpassung. Subklassen mit Listen
     * überschreiben das auf 2.
     * @returns {1 | 2}
     */
    getDefaultRowSpan() {
        return 1;
    }

    /**
     * Setzt die Höhe persistent (wird beim nächsten saveConfig in den State übernommen).
     */
    setRowSpan(span) {
        if (!this.state) this.state = {};
        this.state.rowSpan = span;
    }

    /**
     * Optionale Warnung, die angezeigt wird bevor das Widget entfernt wird.
     * Widgets, die unwiederbringliche Nutzerdaten halten (Notizen, eigene Todos),
     * sollten hier eine erklärende Meldung zurückgeben. null = keine Bestätigung nötig.
     * @returns {string | null}
     */
    getRemovalWarning() {
        return null;
    }

    /**
     * Wird vom Dashboard zur Persistierung des Widget-Zustands aufgerufen.
     * Subklassen die Zustand merken wollen (z.B. ausgewählter MA), überschreiben dies.
     */
    serializeState() {
        return this.state || {};
    }

    setEmpty(message) {
        if (this.bodyEl) {
            this.bodyEl.innerHTML = `<div class="widget-empty">${escapeHtml(message)}</div>`;
        }
    }

    setLoading() {
        if (this.bodyEl) {
            this.bodyEl.innerHTML = '<div class="widget-empty">Lade...</div>';
        }
    }
}

export function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
    }[c]));
}
